import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ApiError, channelName, inboxApi, type TenantChannel } from '@wa/shared';

/**
 * The button that starts a conversation, and the panel behind it.
 *
 * A floating action rather than a header button, which is what the design being
 * matched does and is right for the reason floating actions usually are: the list
 * scrolls and this must not scroll away.
 *
 * <b>New Group is built and gated.</b> Meta's Groups API needs an Official
 * Business Account — the green tick — which is a Meta application rather than
 * anything we build. It says so rather than being hidden, because a missing
 * control is a question ("can it do groups?") and a control that explains itself
 * is an answer.
 */
export function NewChatButton({ onNewChat }: { onNewChat: () => void }) {
  const [fan, setFan] = useState(false);
  const [sheet, setSheet] = useState<null | 'group'>(null);

  useEffect(() => {
    if (!fan) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setFan(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [fan]);

  return (
    <>
      <div className="pointer-events-none absolute right-4 bottom-4 z-20 flex flex-col items-end gap-2">
        {/* Rendered always and revealed, rather than mounted on open: a button
            that appears at full size has no motion to it, and these are meant to
            fan out of the one below them. */}
        {([['group', 'New Group'], ['chat', 'New Chat']] as const).map(([kind, label], i) => (
          <button
            key={kind}
            type="button"
            tabIndex={fan ? undefined : -1}
            aria-hidden={!fan}
            onClick={() => {
              setFan(false);
              if (kind === 'chat') onNewChat(); else setSheet('group');
            }}
            style={{ transitionDelay: fan ? `${i * 40}ms` : '0ms' }}
            className={[
              'tap pointer-events-auto flex items-center gap-2 rounded-(--radius-chip) bg-(--color-brand) px-4 py-2.5',
              'text-body font-medium text-(--color-brand-ink) shadow-(--shadow-pop)',
              'transition-all duration-200 ease-out motion-reduce:transition-none',
              fan ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-2 opacity-0',
            ].join(' ')}
          >
            {kind === 'group' ? <GroupIcon /> : <ChatIcon />}
            {label}
          </button>
        ))}

        <button
          type="button"
          onClick={() => setFan((v) => !v)}
          aria-expanded={fan}
          aria-label={fan ? 'Close' : 'Start something new'}
          className="tap pointer-events-auto flex items-center gap-2 rounded-(--radius-chip) bg-(--color-brand) px-4 py-2.5 text-body font-bold text-(--color-brand-ink) shadow-(--shadow-pop) transition-colors hover:bg-(--color-brand-strong)"
        >
          <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true"
               className={['transition-transform duration-200 ease-out motion-reduce:transition-none',
                           fan ? 'rotate-45' : ''].join(' ')}>
            <path d="M10 4.5v11M4.5 10h11" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
          </svg>
          {/* The word goes when it becomes a close button; the glyph has already
              rotated into an ✕ and "✕ New" would be two instructions at once. */}
          {!fan && 'New'}
        </button>
      </div>

      {sheet === 'group' && <GroupsGate onClose={() => setSheet(null)} />}
    </>
  );
}

/* ---------------------------------------------------------------- new chat -- */

/**
 * The country code is a separate field from the number.
 *
 * Because the two are entered differently: the code is picked once and then never
 * again, and the number is typed every time. Asking for "+919876543210" in one box
 * gets "9876543210" from half the people who use it — a number that is then stored
 * without a country code and silently fails at Meta.
 */
const CODES = [
  { code: '91', flag: '🇮🇳', name: 'India' },
  { code: '1', flag: '🇺🇸', name: 'United States' },
  { code: '44', flag: '🇬🇧', name: 'United Kingdom' },
  { code: '971', flag: '🇦🇪', name: 'United Arab Emirates' },
  { code: '65', flag: '🇸🇬', name: 'Singapore' },
  { code: '61', flag: '🇦🇺', name: 'Australia' },
  { code: '966', flag: '🇸🇦', name: 'Saudi Arabia' },
  { code: '60', flag: '🇲🇾', name: 'Malaysia' },
];

const QUALITY: Record<string, { label: string; tone: string }> = {
  GREEN: { label: 'High', tone: 'text-(--color-good)' },
  YELLOW: { label: 'Medium', tone: 'text-(--color-warn)' },
  RED: { label: 'Low', tone: 'text-(--color-bad)' },
};

export function NewChatPanel({ onClose, onStarted }: {
  onClose: () => void;
  onStarted: (conversationId: number) => void;
}) {
  const [code, setCode] = useState('91');
  const [number, setNumber] = useState('');
  const [channelId, setChannelId] = useState<number | null>(null);
  const [changing, setChanging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const channels = useQuery({ queryKey: ['channels'], queryFn: inboxApi.channels, staleTime: 300_000 });

  const channel = useMemo(() => {
    const list = channels.data ?? [];
    return list.find((c) => c.id === channelId) ?? list[0];
  }, [channels.data, channelId]);

  // Read once. Indexing a Record with a string from the server is `T | undefined`
  // however many times it is checked inline, and three inline checks is three
  // places for one of them to be forgotten.
  const quality = channel?.qualityRating ? QUALITY[channel.qualityRating] : undefined;

  const digits = number.replace(/\D/g, '');
  const ready = digits.length >= 6 && channel !== undefined;

  const start = useMutation({
    mutationFn: () => inboxApi.startChat(`${code}${digits}`, channel!.id),
    onSuccess: (r) => onStarted(r.conversationId),
    onError: (e) => setError(e instanceof ApiError ? e.message : 'That did not work.'),
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    // A screen, not a dialog. The design being matched replaces the list and the
    // thread with this form rather than floating a box over them — which is the
    // right shape for it: entering a number is the whole task while it is
    // happening, and a modal over a chat list invites the reader to keep reading
    // the chat list.
    <section
      aria-label="Start a new chat"
      className="flex h-full min-h-0 flex-col border-r border-(--color-line) bg-(--color-surface)"
    >
      <form
        onSubmit={(e) => { e.preventDefault(); if (ready) start.mutate(); }}
        className="flex min-h-0 flex-1 flex-col"
      >
        <div className="pane-scroll min-h-0 flex-1">
          <div className="px-4 pt-4 pb-5">
            <label htmlFor="new-chat-number" className="block text-small text-(--color-ink-soft)">
              Enter customer&rsquo;s phone number
            </label>

            <div className="mt-2.5 flex items-stretch gap-3">
              <select
                value={code}
                onChange={(e) => setCode(e.target.value)}
                aria-label="Country code"
                className="h-11 shrink-0 rounded-(--radius-field) border border-(--color-brand) bg-(--color-surface) px-2 text-body outline-none"
              >
                {CODES.map((c) => (
                  <option key={c.code} value={c.code}>{c.flag} +{c.code}</option>
                ))}
              </select>

              {/* Underlined, not boxed — the country code is the boxed control and
                  two boxes side by side read as two equal fields rather than as a
                  prefix and a number. */}
              <input
                id="new-chat-number"
                autoFocus
                inputMode="tel"
                autoComplete="tel-national"
                value={number}
                onChange={(e) => { setNumber(e.target.value); setError(null); }}
                aria-label="Phone number"
                /* focus-underline is the documented opt-out from the global focus
                   ring — see styles.css. The underline is this field's focus
                   indicator; a ring around it would draw the box the control is
                   meant not to have. */
                className="focus-underline h-11 min-w-0 flex-1 rounded-none border-x-0 border-t-0 border-b border-(--color-line) bg-transparent px-1 text-lead tabular-nums focus:border-b-2 focus:border-(--color-brand)"
              />
            </div>
          </div>

          <div className="border-t border-(--color-line)" />

          <div className="px-4 py-4">
            <p className="text-small text-(--color-ink-soft)">Send message via</p>

            {channels.isPending && (
              <p className="mt-3 text-small text-(--color-muted)">Loading&hellip;</p>
            )}

            {channel && !changing && (
              <div className="mt-3 flex items-center gap-3">
                <ChannelMark />

                <span className="min-w-0 flex-1">
                  <span className="block truncate text-body">{channel.verifiedName ?? 'Your number'}</span>
                  <span className="block truncate text-small text-(--color-muted) tabular-nums">
                    {channel.displayPhoneNumber.replace(/[^\d]/g, '')}
                  </span>
                </span>

                {/* The quality rating, here rather than only on Home. This is the
                    moment it matters: a number rated Low is one Meta may stop
                    letting you start conversations from, and the agent about to
                    start one is the person who should see it. */}
                {quality && (
                  <span className={`flex shrink-0 items-center gap-1 rounded-(--radius-chip) bg-(--color-brand-wash) px-2 py-1 text-micro font-medium ${quality.tone}`}>
                    <BarsIcon />
                    {quality.label}
                  </span>
                )}

                {(channels.data ?? []).length > 1 && (
                  <button
                    type="button"
                    onClick={() => setChanging(true)}
                    className="tap shrink-0 text-body font-medium text-(--color-brand) hover:underline"
                  >
                    Change
                  </button>
                )}
              </div>
            )}

            {changing && (
              <select
                autoFocus
                value={channel?.id ?? ''}
                onChange={(e) => { setChannelId(Number(e.target.value)); setChanging(false); }}
                className="mt-3 h-11 w-full rounded-(--radius-field) border border-(--color-line) bg-(--color-sunken) px-2.5 text-body outline-none focus:border-(--color-brand)"
              >
                {(channels.data ?? []).map((c: TenantChannel) => (
                  <option key={c.id} value={c.id}>{channelName(c)}</option>
                ))}
              </select>
            )}
          </div>

          <div className="border-t border-(--color-line)" />

          {error && (
            <p role="alert" className="px-4 py-3 text-small text-(--color-bad)">{error}</p>
          )}

          {/* Low and quiet rather than a coloured box beside the field.
              It still has to be said — they have not written in, so the first
              message is a template and opens a billed conversation, and an agent
              who learns that from an invoice was misled by their tools — but it is
              a footnote, not a warning, and the reference has nothing here at all. */}
          <p className="px-4 pt-4 text-micro text-(--color-faint)">
            They have not written in, so the first message has to be an approved
            template and starts a paid conversation.
          </p>
        </div>

        <div className="flex shrink-0 gap-3 border-t border-(--color-line) p-3">
          <button
            type="button"
            onClick={onClose}
            className="tap rounded-(--radius-field) border border-(--color-line) px-6 py-2.5 text-body font-medium hover:bg-(--color-sunken)"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!ready || start.isPending}
            className="tap flex-1 rounded-(--radius-field) bg-(--color-brand) px-3 py-2.5 text-body font-medium text-(--color-brand-ink) transition-colors hover:bg-(--color-brand-strong) disabled:opacity-50"
          >
            {start.isPending ? 'Starting\u2026' : 'Start Chat'}
          </button>
        </div>
      </form>
    </section>
  );
}

/* ------------------------------------------------------------------ groups -- */

function GroupsGate({ onClose }: { onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-40 grid place-items-center bg-black/35 p-4" role="presentation" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Groups"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xs rounded-(--radius-panel) border border-(--color-line) bg-(--color-surface) p-4 text-center shadow-(--shadow-pop)"
      >
        <span aria-hidden="true" className="mx-auto mb-2 grid size-11 place-items-center rounded-full bg-(--color-sunken) text-(--color-muted)">
          <GroupIcon />
        </span>
        <h2 className="text-lead font-semibold tracking-tight">Groups are not switched on</h2>
        {/* Named, not vague. "Coming soon" tells an agent nothing and tells whoever
            would have to chase Meta even less. */}
        <p className="mt-2 text-small text-(--color-muted)">
          WhatsApp group messaging needs an Official Business Account — the green tick —
          which is a Meta application rather than something we build. It also caps a
          group at eight people and supports neither read receipts nor buttons.
        </p>
        <button
          type="button"
          onClick={onClose}
          className="tap mt-4 w-full rounded-(--radius-field) border border-(--color-line) px-3 py-2 text-body font-medium hover:bg-(--color-sunken)"
        >
          Close
        </button>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- glyphs --- */

const stroke = {
  fill: 'none', stroke: 'currentColor', strokeWidth: 1.7,
  strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
};

function ChatIcon() {
  return (
    <svg viewBox="0 0 20 20" width="17" height="17" aria-hidden="true">
      <path d="M3.6 9.4c0-2.9 2.9-5.2 6.4-5.2s6.4 2.3 6.4 5.2-2.9 5.2-6.4 5.2a8 8 0 0 1-1.9-.2l-2.9 1.4.6-2.4a5 5 0 0 1-2.2-4z" {...stroke} />
    </svg>
  );
}

function GroupIcon() {
  return (
    <svg viewBox="0 0 20 20" width="17" height="17" aria-hidden="true">
      <circle cx="7.4" cy="7.8" r="2.2" {...stroke} />
      <path d="M3.2 15.4a4.4 4.4 0 0 1 8.4 0" {...stroke} />
      <path d="M13 5.9a2.1 2.1 0 0 1 0 3.9M16.8 15.4a4.2 4.2 0 0 0-1-2.6" {...stroke} />
    </svg>
  );
}

function ChannelMark() {
  return (
    <span aria-hidden="true" className="relative shrink-0">
      <span className="grid size-10 place-items-center rounded-full bg-(--color-sunken) text-(--color-muted)">
        <svg viewBox="0 0 20 20" width="19" height="19">
          <path d="M10 2.6a7.3 7.3 0 0 0-6.3 11l-.9 3.4 3.5-.9A7.3 7.3 0 1 0 10 2.6z"
                fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
      </span>

      {/* The same badge the list rows carry, for the same reason: it says which
          network, and a second treatment for one screen is a second thing to keep
          in step. */}
      <span className="absolute -right-0.5 -bottom-0.5 grid size-4 place-items-center rounded-full bg-(--color-surface)">
        <svg viewBox="0 0 24 24" width="13" height="13">
          <circle cx="12" cy="12" r="12" fill="#25D366" />
          <path fill="#FFFFFF"
                d="M12 4.9a7.1 7.1 0 0 0-6 10.9l-1 3.6 3.7-1A7.1 7.1 0 1 0 12 4.9Z" />
          <path fill="#25D366"
                d="M9.98 8.86c-.14-.31-.28-.31-.41-.32h-.35c-.12 0-.32.05-.49.23-.17.18-.64.63-.64 1.53 0 .9.66 1.77.75 1.9.09.12 1.28 2.04 3.16 2.78 1.56.62 1.88.5 2.22.46.34-.03 1.09-.44 1.24-.87.15-.43.15-.8.1-.88-.04-.07-.16-.12-.34-.2-.18-.09-1.08-.53-1.25-.6-.17-.06-.29-.09-.41.09-.12.19-.47.6-.58.72-.1.12-.21.13-.39.05-.18-.09-.77-.29-1.46-.9-.54-.48-.9-1.08-1.01-1.26-.1-.18-.01-.28.08-.37.09-.09.19-.22.28-.32.09-.11.12-.19.19-.32.06-.13.03-.24-.02-.33-.05-.09-.41-1-.57-1.36Z" />
        </svg>
      </span>
    </span>
  );
}

/** The three ascending bars beside the quality word, as the reference draws it. */
function BarsIcon() {
  return (
    <svg viewBox="0 0 14 14" width="11" height="11" aria-hidden="true">
      <rect x="1" y="8" width="3" height="5" rx="1" fill="currentColor" />
      <rect x="5.5" y="5" width="3" height="8" rx="1" fill="currentColor" />
      <rect x="10" y="2" width="3" height="11" rx="1" fill="currentColor" />
    </svg>
  );
}
